import { coralRpc } from '../utils/coral';

// Types aligned with IRISCatalogMetadataService Smithy model

export enum CatalogJobStatus {
  PENDING = 'PENDING',
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE',
}

export interface ApiMetadata {
  serviceName: string;
  apiName: string;
  schemaUpdatedAt?: number;
  apiDescription?: string;
  modelPackageLocation?: string;
  originalSchema?: string;
  modifiedSchema?: string;
  // New deterministic-pipeline fields written by the catalog backend.
  // deterministicSchema is the structural source of truth (CoralToOpenApi);
  // aiImprovedSchema is the AI-enriched schema. The legacy originalSchema/
  // modifiedSchema are retained for backward compatibility (to be deprecated).
  deterministicSchema?: string;
  aiImprovedSchema?: string;
  schemaGenerated?: boolean;
  createdAt?: number;
  createdBy?: string;
  modifiedAt?: number;
  modifiedBy?: string;
}

export interface ServiceMetadata {
  serviceName: string;
  apiList: string[];
  namespace?: string;
  endpoints?: string[];
  specgenCommitId?: string;
  specgenBranch?: string;
  specgenLastSyncTime?: number;
  catalogStatus?: CatalogJobStatus;
  catalogJobErrorMessage?: string;
  createdAt?: number;
  createdBy?: string;
  modifiedAt?: number;
  modifiedBy?: string;
}

export interface ListServicesResponse {
  services: ServiceMetadata[];
  nextToken?: string;
}

export interface GetApiNamesByServiceResponse {
  apiNames: string[];
  nextToken?: string;
}

export interface GetApiMetadataResponse {
  apiMetadata: ApiMetadata;
}

export interface GetServiceMetadataResponse {
  serviceMetadata: ServiceMetadata;
}

export interface SubmitCatalogJobResponse {
  status: CatalogJobStatus;
  jobId?: string;
  message?: string;
  catalogJobErrorMessage?: string;
}

export async function listServices(
  nextToken?: string,
  maxResults: number = 20,
  serviceNameSearchTerm?: string,
): Promise<ListServicesResponse> {
  const request: Record<string, unknown> = {
    maxResults,
  };

  if (nextToken) {
    request.nextToken = nextToken;
  }

  if (serviceNameSearchTerm) {
    request.serviceNameSearchTerm = serviceNameSearchTerm;
  }

  return coralRpc<ListServicesResponse>('ListServices', request);
}

export async function getServiceMetadata(serviceName: string): Promise<ServiceMetadata> {
  const response = await coralRpc<GetServiceMetadataResponse>('GetServiceMetadata', {
    serviceName,
  });

  return response.serviceMetadata;
}

export async function getApiNamesByService(
  serviceName: string,
  nextToken?: string,
  maxResults: number = 50,
): Promise<GetApiNamesByServiceResponse> {
  const request: Record<string, unknown> = {
    serviceName,
    maxResults,
  };

  if (nextToken) {
    request.nextToken = nextToken;
  }

  return coralRpc<GetApiNamesByServiceResponse>('GetApiNamesByService', request);
}

export async function getApiMetadata(serviceName: string, apiName: string, signal?: AbortSignal): Promise<ApiMetadata> {
  const response = await coralRpc<GetApiMetadataResponse>('GetApiMetadata', { serviceName, apiName }, signal);

  return response.apiMetadata;
}

export async function submitCatalogJob(serviceName: string, forceSubmit = false): Promise<SubmitCatalogJobResponse> {
  return coralRpc<SubmitCatalogJobResponse>('SubmitCatalogJob', {
    serviceName,
    forceSubmit,
  });
}
